import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';

import { LoadingSpinner } from '@simplywallst/ui-core';
import { Heading, Badge, cn } from '@simplywallst/ui-core/next';
import ChevronRightSolid from '@simplywallst/ui-core/icons/ChevronRightSolid';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';

import { useCreateTransactionPortfolio } from '@/hooks/portfolio/useCreateTransactionPortfolio';
import { ROUTE_PORTFOLIO_TRANSACTIONS_CREATE } from '@/constants/routes';

export const NewTransactionPortfolio = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'transactions-portfolio',
  });

  const { mutate, status } = useCreateTransactionPortfolio({
    onSuccess: ({ createTransactionPortfolio }) => {
      const { id } = createTransactionPortfolio;
      dispatch({
        type: ROUTE_PORTFOLIO_TRANSACTIONS_CREATE,
        payload: { portfolioId: id },
      });
    },
  });

  const classes = {
    container: cn(
      'group/portfolio relative grid size-full items-start overflow-hidden px-x2 py-x3 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent focus-visible:duration-0 sm:p-x3 sm:pb-x2',
      status === 'pending' ? 'cursor-not-allowed' : 'cursor-pointer'
    ),
    header: cn(status === 'pending' ? 'text-softer' : 'text-solid'),
    pill: `text-tiny text-pretty ${
      status === 'pending' ? 'text-softer' : 'text-solid'
    }`,
  };

  return (
    <button
      type="button"
      className={classes.container}
      onClick={() => {
        trackEvent({
          modifier: 'create-portfolio',
          action: 'click',
        });

        mutate({ name: 'Transactions' });
      }}
      disabled={status === 'pending'}
      aria-label="Create transaction portfolio"
    >
      <section data-cy-id="transaction-portfolio-creation">
        <div className="pointer-events-none absolute inset-0 bg-p-white-100 opacity-0 transition-opacity duration-300 group-hover/portfolio:opacity-[.01]" />
        <div className="relative grid gap-x2 text-start @container/tile sm:gap-x3">
          <div className="grid gap-x1_5">
            <div className="grid grid-cols-[max-content_max-content] items-center justify-between">
              <div className="grid grid-cols-[max-content_auto] items-center gap-2">
                <Heading as="h2" className={classes.header}>
                  Create by importing transactions
                </Heading>
                {status === 'pending' ? (
                  <LoadingSpinner styleType="light" />
                ) : (
                  <ChevronRightSolid className="size-5 fill-p-white-100 transition-transform duration-500 group-hover/portfolio:translate-x-2" />
                )}
              </div>
              <Badge variant="solid" tone="attention">
                NEW
              </Badge>
            </div>

            <p className="mb-x1 text-pretty text-xs text-soft">
              Add or import your trade history for accurate analysis and
              detailed returns
            </p>

            <div>
              <span className="mb-x0_5 mr-x1 py-1 text-tiny text-soft">
                Returns analysis
              </span>
              <span className="mb-x0_5 mr-x0_5 inline-grid grid-cols-[max-content_auto] items-center gap-x0_5 rounded-md bg-surface-3 px-2 py-1">
                <span className={classes.pill}>Unrealized</span>
                <TickSolid className="size-5 fill-p-green" />
              </span>
              <span className="mb-x0_5 mr-x0_5 inline-grid grid-cols-[max-content_auto] items-center gap-x0_5 rounded-md bg-surface-3 px-2 py-1">
                <span className={classes.pill}>Realized</span>
                <TickSolid className="size-5 fill-p-green" />
              </span>
              <span className="mb-x0_5 mr-x0_5 inline-grid grid-cols-[max-content_auto] items-center gap-x0_5 rounded-md bg-surface-3 px-2 py-1">
                <span className={classes.pill}>Dividends</span>
                <TickSolid className="size-5 fill-p-green" />
              </span>
              <span className="mb-x0_5 mr-x0_5 inline-grid grid-cols-[max-content_auto] items-center gap-x0_5 rounded-md bg-surface-3 px-2 py-1">
                <span className={classes.pill}>Annualized (IRR)</span>
                <TickSolid className="size-5 fill-p-green" />
              </span>
            </div>
          </div>
        </div>
      </section>
    </button>
  );
};
